/**
 * マスターデータの段階的取得フック（パフォーマンス最適化版）
 * 必要なデータのみを段階的に取得し、初期ロード時間を短縮
 */

import useSWR from 'swr'
import { useCallback, useMemo } from 'react'
import { Craftsman, CompanyVehicle, Truck, TruckDriver } from '@/types'
import { mastersApi } from '@/features/masters/api'

/**
 * マスターデータ取得のオプション
 */
interface UseOptimizedMasterDataOptions {
  /** 職人データを含めるか */
  includeCraftsmen?: boolean
  /** 社用車データを含めるか */
  includeVehicles?: boolean
  /** トラックデータを含めるか */
  includeTrucks?: boolean
  /** トラックドライバーデータを含めるか */
  includeDrivers?: boolean
}

/**
 * マスターデータ取得の戻り値
 */
interface UseOptimizedMasterDataReturn {
  /** 職人データ */
  craftsmen: Craftsman[] | null
  /** 社用車データ */
  companyVehicles: CompanyVehicle[] | null
  /** トラックデータ */
  trucks: Truck[] | null
  /** トラックドライバーデータ */
  truckDrivers: TruckDriver[] | null

  /** 全体のローディング状態 */
  isLoading: boolean
  /** エラー */
  error: Error | null

  /** 職人データのローディング状態 */
  craftsmenLoading: boolean
  /** 社用車データのローディング状態 */
  vehiclesLoading: boolean
  /** トラックデータのローディング状態 */
  trucksLoading: boolean
  /** トラックドライバーデータのローディング状態 */
  driversLoading: boolean

  /** 追加データを読み込む */
  loadAdditionalData: (dataType: 'vehicles' | 'trucks' | 'drivers') => Promise<unknown[]>
  /** データをリフレッシュ */
  refreshData: () => void
}

/**
 * 最適化されたマスターデータ取得フック
 */
export const useOptimizedMasterData = ({
  includeCraftsmen = false,
  includeVehicles = false,
  includeTrucks = false,
  includeDrivers = false,
}: UseOptimizedMasterDataOptions = {}): UseOptimizedMasterDataReturn => {

  const fetchCraftsmen = useCallback(async (): Promise<Craftsman[]> => {
    try {
      const raw = await mastersApi.craftsmen.getAll()
      // APIの型とtypes/craftsmenの差分を吸収
      return raw.map((c: import('@/features/masters/types').Craftsman) => ({
        id: c.id,
        tenant_id: c.tenant_id || '',
        name: c.name,
        craftsman_type_id: c.craftsman_type_id ?? null,
        craftsman_type_name: c.craftsman_type_name ?? null,
        daily_rate: c.daily_rate ?? null,
        is_active: Boolean(c.is_active ?? true),
        created_at: c.created_at || '',
        updated_at: c.updated_at || '',
        created_by: c.created_by ?? null,
        updated_by: c.updated_by ?? null,
      })) as Craftsman[]
    } catch (error) {
      console.error('Failed to fetch craftsmen:', error)
      throw error
    }
  }, [])

  const fetchCompanyVehicles = useCallback(async (): Promise<CompanyVehicle[]> => {
    try {
      return await mastersApi.companyVehicles.getAll()
    } catch (error) {
      console.error('Failed to fetch company vehicles:', error)
      throw error
    }
  }, [])

  const fetchTrucks = useCallback(async (): Promise<Truck[]> => {
    try {
      return await mastersApi.trucks.getAll()
    } catch (error) {
      console.error('Failed to fetch trucks:', error)
      throw error
    }
  }, [])

  const fetchTruckDrivers = useCallback(async (): Promise<TruckDriver[]> => {
    try {
      return await mastersApi.truckDrivers.getAll()
    } catch (error) {
      console.error('Failed to fetch truck drivers:', error)
      throw error
    }
  }, [])

  const {
    data: craftsmen,
    error: craftsmenError,
    isLoading: craftsmenLoading,
    mutate: mutateCraftsmen
  } = useSWR(
    includeCraftsmen ? 'craftsmen' : null,
    fetchCraftsmen,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 30000,
    }
  )

  const {
    data: companyVehicles,
    error: vehiclesError,
    isLoading: vehiclesLoading,
    mutate: mutateVehicles
  } = useSWR(
    includeVehicles ? 'company-vehicles' : null,
    fetchCompanyVehicles,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 30000,
    }
  )

  const {
    data: trucks,
    error: trucksError,
    isLoading: trucksLoading,
    mutate: mutateTrucks
  } = useSWR(
    includeTrucks ? 'trucks' : null,
    fetchTrucks,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 30000,
    }
  )

  const {
    data: truckDrivers,
    error: driversError,
    isLoading: driversLoading,
    mutate: mutateDrivers
  } = useSWR(
    includeDrivers ? 'truck-drivers' : null,
    fetchTruckDrivers,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 30000,
    }
  )

  const loadAdditionalData = useCallback(async (dataType: 'vehicles' | 'trucks' | 'drivers') => {
    try {
      switch (dataType) {
        case 'vehicles':
          if (!includeVehicles) {
            return await mastersApi.companyVehicles.getAll()
          }
          break
        case 'trucks':
          if (!includeTrucks) {
            return await mastersApi.trucks.getAll()
          }
          break
        case 'drivers':
          if (!includeDrivers) {
            return await mastersApi.truckDrivers.getAll()
          }
          break
      }
      return []
    } catch (error) {
      console.error(`Failed to load additional ${dataType} data:`, error)
      throw error
    }
  }, [includeVehicles, includeTrucks, includeDrivers])

  const refreshData = useCallback(() => {
    mutateCraftsmen()
    mutateVehicles()
    mutateTrucks()
    mutateDrivers()
  }, [mutateCraftsmen, mutateVehicles, mutateTrucks, mutateDrivers])

  const isLoading = useMemo(() => {
    const loadingStates = []
    if (includeCraftsmen) loadingStates.push(craftsmenLoading)
    if (includeVehicles) loadingStates.push(vehiclesLoading)
    if (includeTrucks) loadingStates.push(trucksLoading)
    if (includeDrivers) loadingStates.push(driversLoading)
    return loadingStates.some(loading => loading)
  }, [includeCraftsmen, includeVehicles, includeTrucks, includeDrivers, craftsmenLoading, vehiclesLoading, trucksLoading, driversLoading])

  const error = useMemo(() => {
    return craftsmenError || vehiclesError || trucksError || driversError || null
  }, [craftsmenError, vehiclesError, trucksError, driversError])

  return {
    craftsmen: craftsmen || null,
    companyVehicles: companyVehicles || null,
    trucks: trucks || null,
    truckDrivers: truckDrivers || null,

    isLoading,
    error,

    craftsmenLoading,
    vehiclesLoading,
    trucksLoading,
    driversLoading,

    loadAdditionalData,
    refreshData,
  }
}
